import React, { useEffect, useRef, useState } from 'react';

import { API, Storage, graphqlOperation } from 'aws-amplify';
import { Container, Col, Form, Modal, Row, Spinner, Tab, Tabs } from 'react-bootstrap';
import Button from '../../../components/Button';
import { FaFilePdf } from 'react-icons/fa';
import { MdHome } from 'react-icons/md';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import { setRoleId } from '../driverQualificationSlice';
import {
  getSkillsFromDriver,
  hasActiveUsers,
  fetchServiceTeamNotes,
  addServiceTeamNote,
  hasDqfAudits,
} from './operations';
import AdditionalDocuments from '../AdditionalDocuments';
import ServiceNotesTable from './ServiceNotesTable';
import {
  ActionOnBehalfModal,
  Breadcrumbs,
  DocumentErrorNotice,
  ModalConfirmation,
  SubHeader,
} from '../../../components';
import { createQuestions, updateDriverQualification } from '../../../graphql/customMutations';
import * as queries from '../../../graphql/queries';
import { getDriverQualificationRoleId } from '../../../graphql/customQueries';
import useIsMobileScreen from '../../../hooks/useCheckIsMobile';
import { formatIsoToDate } from '../../../utils';
import { fetchPdfLinkUrl, fetchPdfObjectUrl, isPdfMissing } from '../../../utils/fetchPdfObjectUrl';
import PrevCommentsTable from '../CorporateDQTable';
import './styles.scoped.css';

function tabsBorderRadius() {
  // eslint-disable-next-line no-undef
  document.querySelectorAll('.rounded-top-left').forEach(elem => {
    elem.style.borderTopLeftRadius = '15px';
  });
  // eslint-disable-next-line no-undef
  document.querySelectorAll('.rounded-top-right').forEach(elem => {
    elem.style.borderTopRightRadius = '15px';
  });
}

const CorporateDQ = () => {
  const { id } = useParams();
  const { user } = useSelector(state => state.admin);
  const [dq, setDQ] = useState({});
  const [dqfId, setDqfId] = useState();
  const [driverSkillSummary, setDriverSkillSummary] = useState('');
  const [dqOwner, setDqOwner] = useState();
  const [dqPdfDocument, setDqPdfDocument] = useState('');
  const [pdfVersion, setPdfVersion] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState();
  const [isCommentChange, setIsCommentChange] = useState('');
  const [hasOwnership, setHasOwnership] = useState(false);
  const [ownershipButton, setOwnershipButton] = useState({
    disable: false,
    label: '',
  });
  const [reload, setReload] = useState(false);
  const [driverSkills, setDriverSkills] = useState([]);
  const [show, setShow] = useState(false); // for error modal
  const [responseSuccessShow, setResponseSuccessShow] = useState(false);
  const [sentSuccessShow, setSentSuccessShow] = useState(false);
  const [activeUserAlert, setActiveUserAlert] = useState(false);
  const [showActionOnBehalf, setShowActionOnBehalf] = useState(false);
  const [actionOnBehalfLoading, setActionOnBehalfLoading] = useState(false);
  const [showFileTypeModal, setShowFileTypeModal] = useState(false);
  const [togglingClientVisible, setTogglingClientVisible] = useState(false);
  const [hasEval, setHasEval] = useState(false); // CP-836: any AI evaluation on record for this DQF
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector(state => state.admin.user.id);
  const roleId = useSelector(state => state.driverQualification.roleId);
  const { tierData } = useSelector(state => state.newRequest);
  const dqOwnerFullName = useSelector(state => state.admin.user.name);
  const isMobile = useIsMobileScreen();
  const hiddenFileInput = useRef(null);
  const handleClick = () => {
    hiddenFileInput.current.click();
  };

  const [serviceNotes, setServiceNotes] = useState([]);
  const [serviceNoteInput, setServiceNoteInput] = useState('');
  const [serviceNotesLoading, setServiceNotesLoading] = useState(false);

  const loadServiceNotes = async () => {
    setServiceNotesLoading(true);
    const { items } = await fetchServiceTeamNotes(id);
    setServiceNotes(items.map(n => ({ ...n, userName: n.createUserFullName })));
    setServiceNotesLoading(false);
  };

  useEffect(() => {
    if (id) loadServiceNotes();
  }, [id]);

  useEffect(() => {
    if (!roleId && userId) {
      API.graphql({ query: getDriverQualificationRoleId, variables: { id: userId } })
        .then(response => {
          response?.data?.getUser?.appAccess?.items?.forEach(item => {
            dispatch(setRoleId(item.roleId.id));
          });
        })
        .catch(e => console.log('getDQRole error:', e));
    }
  }, [roleId, userId]);

  const handleChange = e => {
    const file = e.target.files[0];
    putPDF(file);
  };

  useEffect(() => {
    setDriverSkillSummary(dq?.driverSkillSummary ?? '');
  }, [dq?.driverSkillSummary]);

  useEffect(() => {
    tabsBorderRadius();
  }, [dq]);

  const getSkills = async driverId => {
    const driver = await getSkillsFromDriver(driverId);
    if (driver?.driverSkills?.length) {
      setDriverSkills(driver?.driverSkills);
    }
  };

  useEffect(() => {
    if (dq?.driverId) {
      getSkills(dq.driverId);
    }
  }, [dq?.driverId]);

  useEffect(() => {
    if (id && dqOwnerFullName) getDQ(id);
  }, [reload, dqOwner, dqOwnerFullName]);

  const putPDF = async pdf => {
    try {
      const result = await Storage.put(`dqf/${dqfId}.pdf`, pdf, {
        level: 'public',
        contentType: 'application/pdf',
      });
      await updateDQDocName(id, pdf?.name, userId);
      if (result) {
        setDQ({ ...dq, dqDocumentName: result.key });
        // Storage.put echoes back the full key ("dqf/<id>.pdf"); re-fetch by version
        // instead so the prefix is not applied twice.
        setPdfVersion(version => version + 1);
        setShowFileTypeModal(true);
      }
    } catch (error) {
      console.log('ERROR UPLOAD PDF', error);
    }
  };

  const handleSetFileType = async fileType => {
    setShowFileTypeModal(false);
    try {
      await API.graphql(graphqlOperation(updateDriverQualification, { input: { id, fileType } }));
      setDQ(prev => ({ ...prev, fileType }));
    } catch (e) {
      console.log('Error setting fileType:', e);
    }
  };


  const createQuestion = async (anId, createUser, text, clientApproverName, aDqOwnerFullName) => {
    const create = {
      driverQualificationQuestionsId: anId,
      user: createUser,
      text: text,
      status: 'In-Progress',
      createUserFullName: clientApproverName || aDqOwnerFullName,
      type: 'Sent',
    };
    try {
      await API.graphql(graphqlOperation(createQuestions, { input: create }));
      return true;
    } catch (e) {
      console.log(`Update Status Error: ${JSON.stringify(e)}`);
      return false;
    }
  };

  const updateDQOwner = async (anId, dqSpecialistUser, aDqOwnerFullName, aUserId) => {
    const input = {
      id: anId,
      dqSpecialistEmail: dqSpecialistUser,
      dqOwnerFullName: aDqOwnerFullName,
      updateUser: aUserId,
    };
    try {
      await API.graphql(graphqlOperation(updateDriverQualification, { input: input }));
      return true;
    } catch (e) {
      console.log(`Update Status Error: ${JSON.stringify(e)}`);
      return false;
    }
  };

  const clientVisibleForStatus = status =>
    ['Sent', 'Client Review', 'Information Requested', 'Approved'].includes(status);

  const isClientVisibleDisabled = status =>
    ['Requested', 'Assembling', 'Cancelled'].includes(status);

  const canToggleClientVisible = roleId === '3' || roleId === '10';

  const handleToggleClientVisible = async () => {
    if (togglingClientVisible) return;
    const newValue = !(dq?.clientVisible ?? clientVisibleForStatus(dq?.status));
    setTogglingClientVisible(true);
    setDQ(prev => ({ ...prev, clientVisible: newValue }));
    try {
      await API.graphql(graphqlOperation(updateDriverQualification, {
        input: { id, clientVisible: newValue },
      }));
    } catch (e) {
      setDQ(prev => ({ ...prev, clientVisible: !newValue }));
      setShow(true);
    } finally {
      setTogglingClientVisible(false);
    }
  };

  const updateDQStatus = async (anId, status, aUserId, clientApproverName) => {
    const update = {
      id: anId,
      status: status,
      clientVisible: clientVisibleForStatus(status),
      dqClientApproverUser: aUserId,
      dqClientApproverUserFullName: clientApproverName,
      driverSkillSummary: driverSkillSummary,
    };

    try {
      await API.graphql(
        graphqlOperation(updateDriverQualification, {
          input: update,
        }),
      );
      return true;
    } catch (e) {
      console.log('Update Status Error:', e);
      return false;
    }
  };

  const updateDQDocName = async (anId, docName) => {
    const update = {
      id: anId,
      dqDocumentName: docName,
    };
    try {
      await API.graphql(
        graphqlOperation(updateDriverQualification, {
          input: update,
        }),
      );
      return true;
    } catch (e) {
      console.log(`Update Status Error: ${JSON.stringify(e)}`);
      return false;
    }
  };

  const getDQByID = async anId => {
    try {
      const response = await API.graphql({
        query: queries.getDriverQualification,
        variables: { id: anId },
      });
      return response.data.getDriverQualification;
    } catch (e) {
      console.log(e);
      return undefined;
    }
  };

  const getDQ = async anId => {
    const response = await getDQByID(anId);
    if (response) {
      setDQ(response);
      setDqfId(response.id);
      setDqOwner(response.dqOwnerFullName);
      if (!response.dqOwnerFullName) {
        setOwnershipButton({
          ...ownershipButton,
          disable: false,
          label: 'Take Ownership',
        });
      }

      if (response?.dqOwnerFullName) {
        setHasOwnership(true);
        if (roleId === '3' || roleId === '10') {
          return response?.dqOwnerFullName !== dqOwnerFullName
            ? setOwnershipButton({
              ...ownershipButton,
              disable: false,
              label: 'Take Ownership',
            })
            : setOwnershipButton({
              ...ownershipButton,
              disable: true,
              label: 'Ownership Taken',
            });
        }
        setOwnershipButton({
          ...ownershipButton,
          disable: true,
          label: 'Ownership Taken',
        });
      }
    }
    return undefined;
  };

  const getPDF = async (pdf, isCancelled = () => false) => {
    setPdfLoading(true);
    try {
      // Mobile opens the document in its own tab, so give the anchor the presigned URL.
      // Desktop embeds it, which must be a blob so no second S3 request is made.
      const url = isMobile ? await fetchPdfLinkUrl(`dqf/${pdf}`) : await fetchPdfObjectUrl(`dqf/${pdf}`);
      if (isCancelled()) {
        if (!isMobile) URL.revokeObjectURL(url);
        return;
      }
      setDqPdfDocument(url);
      setPdfError(false);
    } catch (err) {
      if (isCancelled()) return;
      console.log('This is the error', err);
      setPdfError(isPdfMissing(err) ? 'missing' : 'failed');
    } finally {
      if (!isCancelled()) setPdfLoading(false);
    }
  };

  useEffect(() => {
    if (!(dq?.id > 0)) return undefined;
    let cancelled = false;
    getPDF(`${dq?.id}.pdf`, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [dq?.id, pdfVersion, isMobile]);

  // Release the previous blob when it is replaced, and the last one on unmount.
  useEffect(
    () => () => {
      if (dqPdfDocument?.startsWith('blob:')) URL.revokeObjectURL(dqPdfDocument);
    },
    [dqPdfDocument],
  );

  const handleOwnerClick = async () => {
    const success = await updateDQOwner(id, userId, dqOwnerFullName);
    if (success) {
      setReload(!reload);
      if (dq?.status === 'Requested') {
        setDQ({
          ...dq,
          dqSpecialistEmail: userId,
          status: 'Assembling',
        });
        await updateDQStatus(id, 'Assembling');
      } else {
        setDQ({
          ...dq,
          dqSpecialistEmail: userId,
        });
      }
    } else {
      console.log('Error with setting DQ owner on backend');
    }
  };

  async function handleRespond() {
    const success1 = await createQuestion(id, userId, isCommentChange, dqOwnerFullName);
    let success2;
    if (success1) {
      success2 = await updateDQStatus(id, 'Sent');
    }

    //Create a question and updated a DQ status
    if (success1 && success2) {
      setIsCommentChange('');
      //Show modal if both things are true
      setResponseSuccessShow(true);
    } else {
      setShow(true);
    }
  }

  // CP-836 — has this DQF been AI-evaluated before? Drives the Evaluate-to-Send button states.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const exists = await hasDqfAudits(id);
      if (!cancelled) setHasEval(exists);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Go to the AI Review page and let IT kick off the audit — after AI Review's own verifyRoute runs
  // (so verifyRoute stays the first GraphQL on that screen). AI Review shows the spinner + result.
  const handleEvaluate = () => {
    navigate(`/driver-qualification/ai-review/${id}`, { state: { startAudit: true } });
  };

  const handleSendToCustomer = async () => {
    // check for an active client-contact for this customer
    let activeUsers;
    try {
      activeUsers = await hasActiveUsers(dq.customerId);
    } catch (e) {
      // A query failure must not be reported as "no active users" — surface a generic error instead.
      console.log(e);
      setShow(true);
      return;
    }
    if (activeUsers) {
      if (dq?.status === 'Assembling') {
        setDQ(prev => ({ ...prev, status: 'Sent', clientVisible: true }));
        const newDqStatus = 'Sent';
        const success = await updateDQStatus(id, newDqStatus);
        if (success) {
          setSentSuccessShow(true);
        } else {
          setShow(true);
        }
      }
    } else {
      setActiveUserAlert(true);
    }
  };

  //Any time the questions are updated run a useEffect that get's the DQ,
  //SET state to empty on Modal Close
  const clearFields = () => {
    setSentSuccessShow(false);
    setDQ();
    setDqPdfDocument('');
    setIsCommentChange(null);
    setPdfError();
    setShow(false);
    setResponseSuccessShow(false);
    setSentSuccessShow(false);
    setReload(!reload);
  };

  const getAllSkills = () => {
    let skills = '';
    driverSkills?.forEach(item => {
      skills = skills.concat(`${item?.skillName}: ${item?.skillDescription}, `);
    });
    return skills.substring(0, skills.length - 2);
  };

  const handleActivateDQF = async () => {
    try {
      await updateDQStatus(id, 'Requested');
      setDQ(prev => ({ ...prev, status: 'Requested', clientVisible: false }));
    } catch (e) {
      console.log('e', e);
    }
    setReload(!reload);
  };

  const handleCancelDQF = async () => {
    try {
      await updateDQStatus(id, 'Cancelled');
      await updateDQOwner(id, null, '');
      setDQ(prev => ({ ...prev, status: 'Cancelled', clientVisible: false }));
    } catch (e) {
      console.log('e', e);
    }
    setReload(!reload);
  };

  const userCanEdit = () =>
    hasOwnership &&
    userId === `${dq?.dqSpecialistEmail}` &&
    (dq?.status === 'Assembling' ||
      dq?.status === 'Information Requested' ||
      dq?.status === 'Client Review' ||
      dq?.status === 'Sent');

  const handleApproveOnBehalf = async clientName => {
    try {
      setActionOnBehalfLoading(true);
      await updateDQStatus(id, 'Approved', userId, clientName);
      await API.graphql(graphqlOperation(createQuestions, {
        input: {
          driverQualificationQuestionsId: id,
          user: userId,
          text: `Action on behalf of ${clientName}`,
          status: null,
          createUserFullName: dqOwnerFullName,
          type: 'Approved',
        },
      }));
      setDQ(prev => ({ ...prev, status: 'Approved', clientVisible: true }));
      setShowActionOnBehalf(false);
      setReload(!reload);
    } catch (e) {
      console.log('Error approving on behalf:', e);
    } finally {
      setActionOnBehalfLoading(false);
    }
  };

  const handleRejectOnBehalf = async (clientName, rejectReason) => {
    try {
      setActionOnBehalfLoading(true);
      await updateDQStatus(id, 'Rejected', userId, clientName);
      await API.graphql(graphqlOperation(createQuestions, {
        input: {
          driverQualificationQuestionsId: id,
          user: userId,
          text: `Action on behalf of ${clientName}. ${rejectReason}`,
          status: null,
          createUserFullName: dqOwnerFullName,
          type: 'Rejected',
        },
      }));
      setDQ(prev => ({ ...prev, status: 'Rejected', clientVisible: false }));
      setShowActionOnBehalf(false);
      setReload(!reload);
    } catch (e) {
      console.log('Error rejecting on behalf:', e);
    } finally {
      setActionOnBehalfLoading(false);
    }
  };

  const handleAddServiceNote = async () => {
    if (!serviceNoteInput.trim()) return;
    setServiceNotesLoading(true);
    await addServiceTeamNote(id, user?.id, serviceNoteInput, user?.name);
    setServiceNoteInput('');
    await loadServiceNotes(1);
    setServiceNotesLoading(false);
  };

  return (
    <div style={{ width: '90%' }} className="m-auto">
      <SubHeader
        text="Centerline Driver Qualification"
        buttons={[
          {
            icon: <MdHome size={24} style={{ color: '#fff' }} />,
            onPress: () => navigate('../driver-qualification', { replace: true }),
          },
          {
            text: 'Requested DQs',
            onPress: () => navigate('../driver-qualification/requested', { replace: true }),
            style: { fontWeight: 700 },
          },
        ]}
        popoverTitle="Need help with your driver qualification?"
      />
      <Breadcrumbs
        leftTitle="DQF Home"
        midItemAction={() => navigate(`/driver-qualification/${dq?.status.toLowerCase().replace(/\s+/g, '-')}`)}
        middleTitle={dq?.status !== undefined ? `${dq?.status} Files` : ''}
        route="/driver-qualification"
        rightTitle={id && `File ${id}\u00A0`}
        status={dq?.status}
        handleCancelDQF={handleCancelDQF}
        handleActivateDQF={handleActivateDQF}
        handleActionOnBehalf={() => setShowActionOnBehalf(true)}
        handleAiReview={hasEval ? () => navigate(`/driver-qualification/ai-review/${id}`) : undefined}
      />
      <Container className="vh-100 pb-5 pt-3" fluid>
        <Tabs fill activeKey="pdf">
          <Tab
            eventKey="pdf"
            title="View DQF"
            tabClassName="border border-primary rounded-top-left rounded-top-right bg-primary text-white active">
            <div className="h-100 p-4 border border-primary">
              <Form>
                <Form.Group>
                  <Row style={{ alignItems: 'center', marginBottom: '30px' }}>
                    <Col
                      style={{
                        color: '#c8102e',
                        fontSize: isMobile ? '16px' : '24px',
                        marginBottom: isMobile ? '20px' : '0',
                      }}
                      className="font-weight-bold h4">
                      {dq?.name}
                    </Col>
                    {dq?.status !== 'Cancelled' && (
                      <Button
                        variant="primary"
                        style={{
                          width: '140px',
                          height: '40px',
                        }}
                        disabled={ownershipButton.disable}
                        onPress={() => handleOwnerClick()}
                        text={ownershipButton.label}
                      />
                    )}
                    <Col
                      className="font-weight-bold black-header"
                      style={{
                        display: 'flex',
                        justifyContent: 'right',
                        alignItems: 'center',
                        gap: 8,
                      }}>
                      {dq?.fileType === 'Partial' && (
                        <span
                          style={{
                            backgroundColor: '#C8102E',
                            color: '#fff',
                            padding: '3px 10px',
                            borderRadius: 4,
                            fontSize: 13,
                            fontWeight: 700,
                          }}>
                          Partial
                        </span>
                      )}
                      {dq?.fileType === 'Complete' && (
                        <span
                          style={{
                            backgroundColor: '#53565A',
                            color: '#fff',
                            padding: '3px 10px',
                            borderRadius: 4,
                            fontSize: 13,
                            fontWeight: 700,
                          }}>
                          Complete
                        </span>
                      )}
                      {dq?.id}
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Row>
                        <Col>Driver </Col>
                        <Col className="gray-text">{dq?.driverName}</Col>
                      </Row>
                      <br />
                      <Row>
                        <Col>Generated by</Col>
                        <Col className="gray-text">{dq?.createUserFullName}</Col>
                      </Row>
                      <br />
                    </Col>
                    <Col>
                      <Row>
                        <Col>Job Order</Col>
                        <Col className="gray-text">{dq?.jobId}</Col>
                      </Row>
                      <br />
                      <Row>
                        <Col>Job Start Date</Col>
                        <Col className="gray-text">{formatIsoToDate(dq?.jobStartDate)}</Col>
                      </Row>
                      {!hasOwnership ? (
                        <>
                          <br />
                          <Row>
                            <Col>DQF Specialist</Col>
                            <Col className="gray-text">No Current Owner</Col>
                          </Row>
                          {dq?.tierDescription && (
                            <>
                              <br />
                              <Row>
                                <Col>Tier Desc.</Col>
                                <Col className="gray-text">{dq?.tierDescription}</Col>
                              </Row>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <br />
                          <Row>
                            <Col>DQF Specialist</Col>
                            <Col className="gray-text">{dqOwner}</Col>
                          </Row>
                        </>
                      )}
                    </Col>
                    <Col>
                      <Row>
                        <Col>Status</Col>
                        <Col className="gray-text">{dq?.status}</Col>
                      </Row>
                      <br />
                      <Row>
                        <Col>Created</Col>
                        <Col className="gray-text">{formatIsoToDate(dq?.createdAt)}</Col>
                      </Row>
                      <br />
                      <Row>
                        <Col>Branch</Col>
                        <Col className="gray-text">{dq?.branch}</Col>
                      </Row>
                      <br />
                      <Row>
                        <Col>Tier</Col>
                        <Col className="gray-text">
                          {tierData?.filter(tier => tier.value === dq?.tier)?.[0]?.label}
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row style={{ marginBottom: '20px', marginTop: '20px' }}>
                    <Col>
                      <Row>
                        <Col>City</Col>
                        <Col className="gray-text">{dq?.locationCity?.name}</Col>
                      </Row>
                    </Col>

                    <Col>
                      <Row>
                        <Col>State</Col>
                        <Col className="gray-text">{dq?.locationState?.name}</Col>
                      </Row>
                    </Col>

                    <Col />
                  </Row>

                  <div style={{ border: '2px solid #c8102e', borderRadius: 8, margin: '24px 0', padding: 16 }}>
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>Service Team Notes - NOT Visible to Customer</div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                      <input
                        style={{ flex: 1, padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
                        value={serviceNoteInput}
                        onChange={e => setServiceNoteInput(e.target.value)}
                        placeholder="Add a note..."
                        disabled={serviceNotesLoading}
                      />
                      <Button
                        variant="primary"
                        style={{ minWidth: 80 }}
                        onPress={handleAddServiceNote}
                        disabled={!serviceNoteInput.trim() || serviceNotesLoading}
                        text="Save"
                      />
                    </div>
                    <ServiceNotesTable data={serviceNotes} />
                  </div>

                  <Row>
                    <Col>Summary</Col>
                  </Row>
                  <Row>
                    <Col>
                      <textarea
                        defaultValue={driverSkillSummary || dq?.driverSkillSummary}
                        value={driverSkillSummary}
                        onChange={event => setDriverSkillSummary(event.target.value)}
                        className="summary-input"
                        style={{
                          pointerEvents: userCanEdit() ? 'all' : 'none',
                          paddingLeft: userCanEdit() ? '0px' : '16px',
                          border: !userCanEdit() && 'none',
                        }}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>Skills</Col>
                  </Row>
                  <Row>
                    <Col className="scroll-list">
                      <div className="gray-text" style={{ paddingLeft: '16px' }}>
                        {getAllSkills()}
                      </div>
                    </Col>
                  </Row>
                  <Row className="pt-1 mb-3 mt-3 align-items-end">
                    <Col className="font-weight-bold">Driver Qualification Document</Col>
                    <Col xs="auto" className="d-flex align-items-center" style={{ gap: '10px' }}>
                      <span className="font-weight-bold">Client Visibility</span>
                      <button
                        type="button"
                        onClick={canToggleClientVisible ? handleToggleClientVisible : undefined}
                        disabled={!canToggleClientVisible || !dq?.status || isClientVisibleDisabled(dq?.status) || togglingClientVisible}
                        style={{
                          width: '52px',
                          height: '28px',
                          borderRadius: '14px',
                          border: 'none',
                          cursor: !canToggleClientVisible ? 'default' : (!dq?.status || isClientVisibleDisabled(dq?.status) || togglingClientVisible) ? 'not-allowed' : 'pointer',
                          backgroundColor: (dq?.clientVisible ?? clientVisibleForStatus(dq?.status)) ? '#C8102E' : '#53565A',
                          position: 'relative',
                          padding: 0,
                          opacity: (canToggleClientVisible && (!dq?.status || isClientVisibleDisabled(dq?.status))) ? 0.5 : 1,
                          flexShrink: 0,
                        }}
                        aria-label={`Client Visibility: ${(dq?.clientVisible ?? clientVisibleForStatus(dq?.status)) ? 'On' : 'Off'}`}
                        aria-pressed={!!(dq?.clientVisible ?? clientVisibleForStatus(dq?.status))}
                      >
                        <span
                          style={{
                            position: 'absolute',
                            top: '4px',
                            left: (dq?.clientVisible ?? clientVisibleForStatus(dq?.status)) ? '28px' : '4px',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            backgroundColor: 'white',
                            transition: 'left 0.15s ease',
                          }}
                        />
                      </button>
                    </Col>
                  </Row>
                  {pdfLoading && <Spinner animation="border" />}
                  {!pdfLoading && pdfError === 'missing' && (
                    <DocumentErrorNotice message="No document has been attached to this file." hint={null} />
                  )}
                  {!pdfLoading && pdfError === 'failed' && (
                    <DocumentErrorNotice onRetry={() => setPdfVersion(version => version + 1)} />
                  )}
                  {!pdfLoading &&
                    !pdfError &&
                    dqPdfDocument &&
                    (isMobile ? (
                      <>
                        <span>
                          <a href={dqPdfDocument} data-ajax="false" target="_blank" rel="noreferrer">
                            <FaFilePdf size={60} /> View Document
                          </a>
                        </span>
                      </>
                    ) : (
                      <iframe title="pdfDocument" src={dqPdfDocument} height="600" width="100%" />
                    ))}
                  {/*
               If DQ is Claimed
               AND only if the DQ Owner is the currently logged in user
               can the owner upload a PDF
                 */}
                  {userCanEdit() && process.env.REACT_APP_DQF_VERSION === '2' && (
                    <Row className="p-3">
                      <Button variant="primary" style={{ marginRight: '1rem' }} onPress={handleClick} text="Choose File" />
                      <Button
                        variant="primary"
                        onPress={() => {
                          navigate(`../driver-qualification/assemble-new-dqf/${id}`, { replace: true });
                        }}
                        text="Assemble File"
                      />
                      <input
                        type="file"
                        accept="application/pdf"
                        ref={hiddenFileInput}
                        onChange={handleChange}
                        style={{ display: 'none' }}
                      />
                    </Row>
                  )}
                  {userCanEdit() && process.env.REACT_APP_DQF_VERSION === '1' && (
                    <input
                      type="file"
                      accept="application/pdf"
                      className="p-3"
                      onChange={e => {
                        const file = e.target.files[0];
                        putPDF(file);
                      }}
                    />
                  )}
                  <AdditionalDocuments
                    dqfId={dq?.id}
                    dqStatus={dq?.status}
                    isServiceTeam
                    currentUserEmail={userId}
                    currentUserFullName={dqOwnerFullName}
                  />
                  {dq?.questions && (
                    <Row className="padding-row">
                      <Col>
                        <PrevCommentsTable handleSetShowInParent={setShow} reload={reload} />
                      </Col>
                    </Row>
                  )}
                  <Row className="p-4 d-flex justify-content-center">
                    {/* CP-836 Evaluate-to-Send state machine: no eval yet -> Evaluate to Send only;
                        once evaluated -> Evaluate Again + Send to Customer both show. */}
                    {hasOwnership && dq?.dqDocumentName && !hasEval && (
                      <Button
                        variant="primary"
                        className="w-50"
                        onPress={handleEvaluate}
                        text="Evaluate to Send"
                      />
                    )}
                    {hasOwnership && dq?.dqDocumentName && hasEval && (
                      <>
                        <Button
                          variant="outline"
                          className="w-25 mx-2"
                          onPress={handleEvaluate}
                          text="Evaluate Again"
                        />
                        <Button
                          variant="primary"
                          className="w-25 mx-2"
                          onPress={() => handleSendToCustomer()}
                          text="Send to Customer"
                        />
                      </>
                    )}
                    {hasOwnership && dq?.status === 'Information Requested' && (
                      <Row style={{ width: '100%' }}>
                        <Row className="p-1">
                          <Col className="font-weight-bold">Respond to Client</Col>
                        </Row>
                        <Row style={{ width: '100%' }}>
                          <Col>
                            <Form.Group>
                              <Form.Control
                                className="border border-dark"
                                as="textarea"
                                rows={6}
                                value={isCommentChange}
                                onChange={e => {
                                  setIsCommentChange(e.target.value);
                                }}
                              />
                            </Form.Group>
                          </Col>
                          <Button
                            variant="primary"
                            className="w-50"
                            style={{
                              marginLeft: '25%',
                              marginRight: '25%',
                            }}
                            disabled={!isCommentChange}
                            onPress={() => handleRespond('Sent')}
                            text="Respond"
                          />
                        </Row>
                      </Row>
                    )}
                    {show && (
                      <ModalConfirmation
                        title="Error"
                        body="There was an error updating the Driver Qualification. Please contact support."
                        primaryButtonTitle="Close"
                        onClick={() => {
                          setShow(false);
                        }}
                        isOpen={show}
                        primaryButton
                      />
                    )}
                    {responseSuccessShow && (
                      <ModalConfirmation
                        title="Success!"
                        body="Your response was sent."
                        primaryButtonTitle="Close"
                        onClick={() => {
                          //Inside the modal when "Close" is pressed clear the fields
                          // setResponseSuccessShow(false);
                          clearFields();
                        }}
                        isOpen={responseSuccessShow}
                        primaryButton
                      />
                    )}
                    {sentSuccessShow && (
                      <ModalConfirmation
                        title="Success!"
                        body="The Driver Qualification has been sent."
                        primaryButtonTitle="Close"
                        onClick={() => {
                          setSentSuccessShow(false);
                          navigate('/driver-qualification', {
                            replace: true,
                          });
                        }}
                        isOpen={sentSuccessShow}
                        primaryButton
                      />
                    )}
                    {activeUserAlert && (
                      <ModalConfirmation
                        title="Unable to send file."
                        body="There are no active users for this client."
                        primaryButtonTitle="Close"
                        onClick={() => {
                          setActiveUserAlert(false);
                        }}
                        isOpen
                        primaryButton
                      />
                    )}
                    {showActionOnBehalf && (
                      <ActionOnBehalfModal
                        isOpen={showActionOnBehalf}
                        onClose={() => setShowActionOnBehalf(false)}
                        onApprove={handleApproveOnBehalf}
                        onReject={handleRejectOnBehalf}
                        userEmail={user?.id}
                        userName={user?.name}
                        isLoading={actionOnBehalfLoading}
                      />
                    )}
                    <Modal show={showFileTypeModal} style={{ zIndex: 100000 }}>
                      <Modal.Header>
                        <Modal.Title>Success!</Modal.Title>
                      </Modal.Header>
                      <Modal.Body>
                        Your file was successfully attached. Is the file a Complete File (All Checklist Items) or Is the file a Partial File (Missing Checklist Items).
                      </Modal.Body>
                      <Modal.Footer style={{ justifyContent: 'center', gap: 12 }}>
                        <Button variant="primary" text="Complete" onPress={() => handleSetFileType('Complete')} />
                        <Button variant="outline" text="Partial" onPress={() => handleSetFileType('Partial')} />
                      </Modal.Footer>
                    </Modal>
                  </Row>
                </Form.Group>
              </Form>
            </div>
          </Tab>
        </Tabs>
      </Container>
    </div>
  );
};

export default CorporateDQ;
